import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/**
 * ProtectedRoute component that handles authentication and authorization
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - The protected content
 * @param {string} props.requiredRole - Optional required role
 * @param {string} props.permission - Optional permission key (e.g., 'campaigns:view')
 */
function ProtectedRoute({ children, requiredRole, permission }) {
  const { isAuthenticated, isLoading, setupRequired, user, hasPermission } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking auth
  if (isLoading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p style={styles.loadingText}>Loading...</p>
      </div>
    );
  }

  // Redirect to setup if required
  if (setupRequired) {
    return <Navigate to="/setup" replace />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role if required
  if (requiredRole) {
    const userRole = user?.user?.role;

    // Super admin can access everything
    if (userRole === 'super_admin') {
      return children;
    }

    // Check specific role requirement
    if (userRole !== requiredRole) {
      return (
        <div style={styles.accessDenied}>
          <h2>Access Denied</h2>
          <p>You don't have permission to access this page.</p>
          <button
            onClick={() => window.history.back()}
            style={styles.backButton}
          >
            Go Back
          </button>
        </div>
      );
    }
  }

  // Check permission if required
  if (permission && !hasPermission(permission)) {
    return (
      <div style={styles.accessDenied}>
        <h2>Access Denied</h2>
        <p>You don't have permission to access this page.</p>
        <button
          onClick={() => window.history.back()}
          style={styles.backButton}
        >
          Go Back
        </button>
      </div>
    );
  }

  return children;
}

/**
 * Component that only renders for Super Admin users
 */
export function SuperAdminOnly({ children }) {
  return (
    <ProtectedRoute requiredRole="super_admin">
      {children}
    </ProtectedRoute>
  );
}

/**
 * Component that conditionally renders content based on role
 */
export function RoleBasedContent({ superAdminContent, practiceAdminContent, clinicalAdminContent, careProviderContent, defaultContent }) {
  const { user } = useAuth();
  const role = user?.user?.role;

  if (role === 'super_admin' && superAdminContent) return superAdminContent;
  if (role === 'practice_admin' && practiceAdminContent) return practiceAdminContent;
  if (role === 'clinical_admin' && clinicalAdminContent) return clinicalAdminContent;
  if (role === 'care_provider' && careProviderContent) return careProviderContent;

  return defaultContent || null;
}

/**
 * Component that conditionally renders children based on permission
 * Usage: <PermissionGate permission="billing:view"><BillingSection /></PermissionGate>
 */
export function PermissionGate({ permission, children, fallback = null }) {
  const { hasPermission } = useAuth();

  if (!hasPermission(permission)) {
    return fallback;
  }

  return children;
}

const styles = {
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: '#f5f6f8',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #e0e0e0',
    borderTop: '4px solid #667eea',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    marginTop: '16px',
    color: '#666',
    fontSize: '14px',
  },
  accessDenied: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: '#f5f6f8',
    textAlign: 'center',
    padding: '20px',
  },
  backButton: {
    marginTop: '20px',
    padding: '10px 24px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#fff',
    background: '#667eea',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
  },
};

// Add CSS animation for spinner
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);

export default ProtectedRoute;
